import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CaretDownOutlined,
  CaretUpOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { Button, Dropdown, Input } from 'antd';
import { cn } from '@/libs/utils';

// ---------------------------------------------------------------------------
// ReportSortButton — reads/writes sort state via searchParams/updateParams
// ---------------------------------------------------------------------------
export function ReportSortButton({ field, searchParams, updateParams }) {
  const sortKey = `sort${field.charAt(0).toUpperCase() + field.slice(1)}`;
  const current = searchParams.get(sortKey) || null;

  const handleSort = useCallback(() => {
    // Cycle: null → asc → desc → null
    const next = current === null ? 'asc' : current === 'asc' ? 'desc' : null;

    // Clear all other sort keys first
    const clearKeys = {};
    for (const [k] of searchParams.entries()) {
      if (k.startsWith('sort') && k !== sortKey) {
        clearKeys[k] = undefined;
      }
    }
    updateParams({ ...clearKeys, [sortKey]: next, offset: '1' });
  }, [current, sortKey, searchParams, updateParams]);

  return (
    <Button
      type="default"
      onClick={handleSort}
      className={cn(
        'btn-default !align-center grid !h-max p-0 px-0.5',
        current ? 'bg-white text-accent shadow-md' : 'bg-transparent'
      )}
    >
      <div className="grid">
        <CaretUpOutlined
          className={cn(
            'relative top-0.5 scale-[0.8] text-[10px] text-sm text-zinc-400',
            current === 'asc' ? '!text-accent' : ''
          )}
        />
        <CaretDownOutlined
          className={cn(
            'relative bottom-0.5 scale-[0.8] text-[10px] text-sm text-zinc-400',
            current === 'desc' ? '!text-accent' : ''
          )}
        />
      </div>
    </Button>
  );
}

// ---------------------------------------------------------------------------
// ReportSearchFilter — search dropdown for a text column
// ---------------------------------------------------------------------------
export function ReportSearchFilter({ field, title, searchParams, updateParams }) {
  const { t } = useTranslation('forms');
  const currentValue = searchParams.get(field) || '';
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState(currentValue);

  const handleSubmit = useCallback(() => {
    updateParams({
      [field]: inputValue || undefined,
      offset: '1',
    });
    setOpen(false);
  }, [field, inputValue, updateParams]);

  const handleReset = useCallback(() => {
    setInputValue('');
    updateParams({ [field]: undefined, offset: '1' });
    setOpen(false);
  }, [field, updateParams]);

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter') handleSubmit();
    },
    [handleSubmit]
  );

  return (
    <div className="h-max" onKeyDown={handleKeyDown}>
      <Dropdown
        placement="bottomRight"
        open={open}
        trigger={['click']}
        onOpenChange={(v) => {
          setOpen(v);
          if (v) setInputValue(currentValue);
        }}
        popupRender={() => (
          <div className="grid min-w-[180px] gap-3 rounded-md border-[3px] border-accent bg-white p-3 shadow-md">
            <label htmlFor={field} className="font-semibold">
              {title}:
            </label>
            <Input
              id={field}
              autoFocus
              value={inputValue}
              placeholder={title}
              className="h-9 w-full rounded-[50px]"
              onChange={(e) => setInputValue(e.target.value)}
            />
            <div className="flex gap-3">
              <Button
                size="small"
                type="default"
                className="btn-default btn-default--hover h-8 w-1/2"
                onClick={handleReset}
              >
                {t('reset')}
              </Button>
              <Button
                size="small"
                type="primary"
                className="btn-primary h-8 w-1/2"
                onClick={handleSubmit}
              >
                {t('submit')}
              </Button>
            </div>
          </div>
        )}
      >
        <Button
          type="default"
          className="btn-default h-6 w-6 bg-transparent p-0 shadow-none"
          icon={
            <SearchOutlined
              className={cn(
                'rounded-full p-1.5',
                currentValue
                  ? 'bg-white !text-accent shadow-md'
                  : 'text-zinc-400'
              )}
            />
          }
        >
          <span className="sr-only">{t('search')}</span>
        </Button>
      </Dropdown>
    </div>
  );
}

// ---------------------------------------------------------------------------
// HeaderTitle — wrapper for column title with search/sort icons
// ---------------------------------------------------------------------------
export function HeaderTitle({ title, children }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span>{title}</span>
      <div className="flex items-center gap-2">{children}</div>
    </div>
  );
}
